import React, { useEffect, useRef, useState } from "react";
import { matchRoutes, Route, Routes, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { hideMobileSplash } from "../utils/hideMobileSplash";

// Mobile components - MobileHome is eager, others are lazy
import { MobileHome } from "../Pages/ResponsivePages/MobileHome";

// Hook
import { useIsMobile } from "../hooks/useIsMobile";
import { getAuthorProfilePath } from "../constants/TopAuthors";

// Lazy-loaded desktop components (Header/Footer included)
const HeaderPage = React.lazy(() => import("./HeaderPage"));
const FooterPage = React.lazy(() => import("./FooterPage"));

// Desktop pages - all lazy loaded
const HomePage = React.lazy(() => import("../Pages/HomePage"));
const AboutPage = React.lazy(() => import("../Pages/AboutPage"));
const Profile = React.lazy(() => import("../Pages/Profile"));
const EpubPage = React.lazy(() => import("../Pages/Epub"));
const AudiosDetailPage = React.lazy(() => import("../Pages/AudioPlayerPage"));
const AudioPlayerPageNew = React.lazy(() => import("../Pages/AudioPlayerPageNew"));
const BookDetailPage = React.lazy(() => import("../Pages/BookDetailPage"));
const KalyansPage = React.lazy(() => import("../Pages/KalyansPage"));
const KalyanDetailPage = React.lazy(() => import("../Pages/KalyanDetailPage"));
const PravachansPage = React.lazy(() => import("../Pages/PravachansPage"));
const AudiosPage = React.lazy(() => import("../Pages/AudiosPage"));
const ArticlesPage = React.lazy(() => import("../Pages/ArticlesPage"));
const ArticlesDetailPage = React.lazy(() => import("../Pages/ArticlesDetail"));
const KalpataruPage = React.lazy(() => import("../Pages/KalpataruPage"));
const KalpataruDetailPage = React.lazy(() => import("../Pages/KalpataruDetailPage"));
const GeetgovindPage = React.lazy(() => import("../Pages/GeetgovindPage"));
const GeetGovindDetailPage = React.lazy(() => import("../Pages/GeetGovindDetailPage"));
const PrivacyPage = React.lazy(() => import("../Pages/PrivacyPolicyPage"));
const TearmsOfUsePage = React.lazy(() => import("../Pages/TermsOfUsePage"));
const FaqPage = React.lazy(() => import("../Pages/FaqPage"));
const AuthorsDataPage = React.lazy(() => import("../Pages/AuthorsPage"));
const SearchDataPage = React.lazy(() => import("../Pages/SearchDataPage"));
const ProfileFav = React.lazy(() => import("../Pages/ProfileFav").then(m => ({ default: m.ProfileFav })));
const ProfileContact = React.lazy(() => import("../Pages/ProfileContact").then(m => ({ default: m.ProfileContact })));
const VivekvaniPage = React.lazy(() => import("../Pages/VivekVani"));
const DivineQuotes = React.lazy(() => import("../Pages/DivineQuotePage"));
const DailySatsangPage = React.lazy(() => import("../Pages/DailySatsang"));
const PromotionPage = React.lazy(() => import("../Pages/PromotionPage"));
const PDFViewer = React.lazy(() => import("../Pages/PDFViewer"));
const FullImage = React.lazy(() => import("../Pages/FullImage"));
const NewLoginPage = React.lazy(() => import("../Pages/NewLoginPage"));
const VideoListPage = React.lazy(() => import("../Pages/VideoListPage"));
const MaintencePage = React.lazy(() => import("../Pages/MaintencePage"));
const InternetPage = React.lazy(() => import("../Pages/NoInternetConnection"));
const WebViewforbook = React.lazy(() => import("../Pages/WebView"));
const VivekvaniDetailPage = React.lazy(() => import("../Pages/VivekvaniDetailPage"));
const MagazineLandingPage = React.lazy(() => import("../Pages/MagazineLandingPage"));
const AudioLandingPage = React.lazy(() => import("../Pages/AudioLandingPage"));
const AppDownloadPopup = React.lazy(() => import("../Components/AppDownloadPopup"));
const VideoDetailsPage = React.lazy(() => import("../Pages/VideoDetailsPage"));
const QuoteDetailsPage = React.lazy(() => import("../Pages/QuoteDetailsPage"));
const MessagesPage = React.lazy(() => import("../Pages/MesagesPage"));
const MessageDetailPage = React.lazy(() => import("../Pages/MessageDetail"));

// Mobile pages - all lazy loaded
const MobileAboutUs = React.lazy(() => import("../Pages/ResponsivePages/MobileAboutUs"));
const MArticlesPage = React.lazy(() => import("../Pages/ResponsivePages/mArticlesPage"));
const MobileBooksRoute = React.lazy(() => import("../Pages/ResponsivePages/MobileBooksRoute"));
const MobileEpub = React.lazy(() => import("../Pages/ResponsivePages/Common/MobileEpub"));
const MobileMagazines = React.lazy(() => import("../Pages/ResponsivePages/MobileMagazines"));
const MKalyanRoute = React.lazy(() => import("../Pages/ResponsivePages/mKalyanRoute"));
const MKalyanListPage = React.lazy(() => import("../Pages/ResponsivePages/mKalyanListPage"));
const MKalpataruRoute = React.lazy(() => import("../Pages/ResponsivePages/mKalpataruRoute"));
const MKalpataruListPage = React.lazy(() => import("../Pages/ResponsivePages/mKalpataruListPage"));
const MVivekvaniListPage = React.lazy(() => import("../Pages/ResponsivePages/mVivekvaniListPage"));
const MGeetGovindRoute = React.lazy(() => import("../Pages/ResponsivePages/mGeetGovindRoute"));
const MGeetGovindListPage = React.lazy(() => import("../Pages/ResponsivePages/mGeetGovindListPage"));
const MPravachanHubPage = React.lazy(() => import("../Pages/ResponsivePages/mPravachanHubPage"));
const MobileAudioListPage = React.lazy(() => import("../Pages/ResponsivePages/MobileAudioListPage"));
const MbAudioPlayerPage = React.lazy(() => import("../Pages/ResponsivePages/Common/MbAudioPlayerPage"));
const MbAllVideos = React.lazy(() => import("../Pages/ResponsivePages/MbAllVideos"));
const MobileDivineQuotes = React.lazy(() => import("../Pages/ResponsivePages/MobileDivineQuotes"));
const MDivineQuotes = React.lazy(() => import("../Pages/ResponsivePages/mDivineQuotes"));
const MobileProfilePage = React.lazy(() => import("../Pages/ResponsivePages/MobileProfilePage"));
const MobileFavoriatePage = React.lazy(() => import("../Pages/ResponsivePages/MobileFavoriatePage"));
const MobilePrivacyPolicy = React.lazy(() => import("../Pages/ResponsivePages/MobilePrivacyPolicy"));
const MobileTermsOfUse = React.lazy(() => import("../Pages/ResponsivePages/MobileTermsOfUse"));
const MobileLoginPage = React.lazy(() => import("../Pages/ResponsivePages/LoginPage"));
const MBookDetailPage = React.lazy(() => import("../Pages/ResponsivePages/mBookDetailPage"));
const MMagazineHomePage = React.lazy(() => import("../Pages/ResponsivePages/mMagazineHomePage"));
const MobileBooksFilterPage = React.lazy(() => import("../Pages/ResponsivePages/MobileBooksFilterPage"));
const DivineQuotesImageview = React.lazy(() => import("../Pages/ResponsivePages/DivineQuotesImageview"));
const FilteredSubjectData = React.lazy(() => import("../Pages/ResponsivePages/FilteredSubjectData"));
const MobileRecentlyViewed = React.lazy(() => import("../Pages/ResponsivePages/MobileRecentlyViewed"));
const ReadArticles = React.lazy(() => import("../Pages/ResponsivePages/Common/ReadArticles"));
const MobilePromotion = React.lazy(() => import("../Pages/ResponsivePages/MobilePromotion"));
const MAuthorDetailsPage = React.lazy(() => import("../Pages/ResponsivePages/mAuthorDetailsPage"));
const MbAudioCategory = React.lazy(() => import("../Pages/ResponsivePages/MbAudioCategory"));
const MbVideoParentCategory = React.lazy(() => import("../Pages/ResponsivePages/MbVideoParentCategory"));
const CategoriesFilter = React.lazy(() => import("../Pages/ResponsivePages/CategoriesFilter"));
const MobileEnglishTemplate = React.lazy(() => import("../Pages/ResponsivePages/MobileEnglishTemplate"));
const MobileHindiTemplate = React.lazy(() => import("../Pages/ResponsivePages/MobileHindiTemplate"));

const BooksListPage = React.lazy(
  () => import("../Pages/BooksListPage" /* webpackChunkName: "epub" */)
);

// Dev-only test page — uncomment import + /test route below when needed
// const TestPage = React.lazy(() => import("../Pages/TestPage"));

const WithHeaderFooter = ({ element }: { element: any }) => {
  const [showAppPopup, setShowAppPopup] = useState(false);
  const [containerLeft, setContainerLeft] = useState(0);
  const [bannerTop, setBannerTop] = useState(0);

  const measureAndShow = () => {
    if (window.innerWidth >= 1200) {
      const containers = document.querySelector('.containers');
      const header = document.querySelector('.gst-header');
      if (containers) {
        setContainerLeft(containers.getBoundingClientRect().left);
      }
      if (header) {
        setBannerTop(header.getBoundingClientRect().bottom + 12);
      } else {
        setBannerTop(180);
      }
      setShowAppPopup(true);
    }
  };

  useEffect(() => {
    const shouldShow = sessionStorage.getItem('showAppPopup');
    if (shouldShow) {
      measureAndShow();
    }
  }, []);

  useEffect(() => {
    const handler = () => measureAndShow();
    window.addEventListener('appPopupReady', handler);
    return () => window.removeEventListener('appPopupReady', handler);
  }, []);

  return (
    <>
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          boxSizing: "border-box",
          /* "clip" (not "hidden") — hidden makes this div a scroll container,
             which breaks position:sticky for descendants (bottom audio player) */
          overflowX: "clip",
        }}
      >
        <React.Suspense fallback={<div style={{ minHeight: "175px" }}></div>}>
          <HeaderPage />
        </React.Suspense>
        <React.Suspense fallback={<div style={{ minHeight: "calc(100vh - 350px)" }}></div>}>
          {element}
        </React.Suspense>
        <React.Suspense fallback={<div style={{ minHeight: "250px" }}></div>}>
          <FooterPage />
        </React.Suspense>
      </div>
      {/* <AppDownloadPopup
        show={showAppPopup}
        containerLeft={containerLeft}
        bannerTop={bannerTop}
        onClose={() => {
          setShowAppPopup(false);
          sessionStorage.removeItem('showAppPopup');
        }}
      /> */}
    </>
  );
};

// const NotFoundRedirect = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const lastValidPath = useRef("/");

//   useEffect(() => {
//     // 👇 build route definitions directly from your JSX (no duplication)
//     const routes = [
//       { path: "/fullimage" },
//       { path: "/" },
//       { path: "/home" },
//       { path: "/about" },
//       { path: "/books" },
//       { path: "/books/special" },
//       { path: "/books/author/:id" },
//       { path: "/books/language/:langid" },
//       { path: "/books/category/:catid" },
//       { path: "/books/:bookid" },
//       { path: "/books/special/:bookid" },
//       { path: "/books/author/:authorId/:bookid" },
//       { path: "/books/language/:langId/:bookid" },
//       { path: "/books/category/:catId/:bookid" },
//       { path: "/reader/:type/:id" },
//       { path: "/vivekvani" },
//       { path: "/vivekvani/special" },
//       { path: "/vivekvani/author/:id" },
//       { path: "/vivekvani/language/:langid" },
//       { path: "/vivekvani/:cat/:langid" },
//       { path: "/vivekvani/:vaniid" },
//       { path: "/vivekvani/special/:vaniid" },
//       { path: "/vivekvani/author/:id/:vaniid" },
//       { path: "/vivekvani/category/:catid" },
//       { path: "/vivekvani/language/:langid" },
//       { path: "/kalyan" },
//       { path: "/kalyan/category/:catid" },
//       { path: "/kalyan/special" },
//       { path: "/kalyan/author/:id" },
//       { path: "/kalyan/language/:langid" },
//       { path: "/kalyan/:kalyanid" },
//       { path: "/kalyan/special/:kalyanid" },
//       { path: "/kalyan/author/:id/:kalyanid" },
//       { path: "/kalyan/category/:catid/:kalyanid" },
//       { path: "/kalyan/language/:langid/:kalyanid" },
//       { path: "/kalyanakalpataru" },
//       { path: "/kalyanakalpataru/category/:catid" },
//       { path: "/kalyanakalpataru/special" },
//       { path: "/kalyanakalpataru/author/:id" },
//       { path: "/kalyanakalpataru/language/:langid" },
//       { path: "/kalyanakalpataru/:kalyanid" },
//       { path: "/kalyanakalpataru/special/:kalyanid" },
//       { path: "/kalyanakalpataru/author/:id/:kalyanid" },
//       { path: "/kalyanakalpataru/category/:catid/:kalyanid" },
//       { path: "/kalyanakalpataru/language/:langid/:kalyanid" },
//       { path: "/geetgovind" },
//       { path: "/geetgovind/category/:catid" },
//       { path: "/geetgovind/special" },
//       { path: "/geetgovind/author/:id" },
//       { path: "/geetgovind/language/:langid" },
//       { path: "/geetgovind/:id" },
//       { path: "/geetgovind/special/:id" },
//       { path: "/geetgovind/author/:id" },
//       { path: "/geetgovind/category/:catid/:id" },
//       { path: "/geetgovind/language/:langid/:id" },
//       { path: "/pravachans" },
//       { path: "/pravachans/special" },
//       { path: "/pravachans/author/:id" },
//       { path: "/pravachans/:id" },
//       { path: "/pravachans/:cat/:id" },
//       { path: "/audios" },
//       { path: "/audios/special" },
//       { path: "/audios/author/:id" },
//       { path: "/audios/:id" },
//       { path: "/audios/:cat/:id" },
//       { path: "/DailySatsang/:id" },
//       { path: "/DailySatsang" },
//       { path: "/videos" },
//       { path: "/videos/author/:id" },
//       { path: "/videos/category/:id" },
//       { path: "/videos/video/:id" },
//       { path: "/articles" },
//       { path: "/articles/special" },
//       { path: "/articles/author/:id" },
//       { path: "/articles/:id" },
//       { path: "/articles/special/:id" },
//       { path: "/articles/author/:id/:articleid" },
//       { path: "/divinequotes" },
//       { path: "/privacypolicy" },
//       { path: "/termsofuse" },
//       { path: "/faq" },
//       { path: "/promotion" },
//       { path: "/author/:id" },
//       { path: "/searchdata/:searchCategory/:search" },
//       { path: "/searchdata/:searchCategory/" },
//       { path: "/searchdata/:searchCategory/*" },
//       { path: "/profile/" },
//       { path: "/profile/My-Favourites" },
//       { path: "/profile/feedback" },
//       { path: "/pdf-viewer" },
//     ];

//     const matches = matchRoutes(routes, location);

//     if (matches) {
//       lastValidPath.current = location.pathname;
//     } else {
//       navigate(-1 as any);
//       // navigate(-1 as any, { replace: true });
//       // navigate(lastValidPath.current, { replace: true });
//     }
//   }, [location, navigate]);

//   return null;
// };


/** Redirect while preserving location.state (hooks cannot run inside Route element IIFEs). */
const NavigatePreserveState = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={to} replace state={location.state} />;
};

/** Old nested article-detail URLs → canonical /articles/:slug */
const RedirectToCanonicalArticle = ({ slugParam }: { slugParam: string }) => {
  const location = useLocation();
  const params = useParams();
  const slug = params[slugParam];
  if (!slug) {
    return <Navigate to="/articles" replace />;
  }
  return <Navigate to={`/articles/${slug}`} replace state={location.state} />;
};

/**
 * Legacy /legendsIntroduction → /author/{properSlug}.
 * Maps optional state.id to slug; never puts Mongo id in the URL.
 */
const RedirectLegendsIntroduction = () => {
  const location = useLocation();
  const stateId = (location.state as { id?: string } | null)?.id;
  const path = getAuthorProfilePath(stateId);
  return <Navigate to={path} replace />;
};

const AppRoutes = () => {
  const isMobile = useIsMobile();
  const location = useLocation();

  useEffect(() => {
    if (!isMobile) return;

    // Any direct mobile route should dismiss the splash once the app shell paints.
    const logdetails: string[] = [
      'mobile-route splash dismiss',
      `path=${location.pathname || 'unknown'}`,
    ];
    hideMobileSplash('AppRoutes', logdetails);
  }, [isMobile, location.pathname]);

  return (
    <Routes>

      <Route element={<WebViewforbook />} path="/webView"></Route>
      {/* full image path */}
      <Route element={<FullImage />} path="/fullimage"></Route>

      {/* Home */}
      <Route
        element={
          isMobile
            ? <MobileHome />
            : <WithHeaderFooter element={<HomePage />} />
        }
        path="/"
      />

      {/* About */}
      <Route
        element={
          isMobile
            ? <MobileAboutUs />
            : <WithHeaderFooter element={<AboutPage />} />
        }
        path="/about"
      ></Route>

      {/* Magazine Landing */}
      <Route
        element={
          isMobile
            ? <MobileMagazines />
            : <WithHeaderFooter element={<MagazineLandingPage />} />
        }
        path="/magazines"
      ></Route>
      <Route
        element={<Navigate to="/magazines" replace />}
        path="/magazine"
      ></Route>

      {/* Audio Landing */}
      <Route
        element={<WithHeaderFooter element={<AudioLandingPage />} />}
        path="/Audio"
      ></Route>

      {/* Books */}
      <Route
        element={
          isMobile
            ? <MobileBooksRoute />
            : <WithHeaderFooter element={<BooksListPage />} />
        }
        path="/books"
      ></Route>
      {/* Dev-only test page — uncomment when needed
      <Route
        element={<WithHeaderFooter element={<TestPage />} />}
        path="/test"
      ></Route>
      */}
      <Route
        element={
          isMobile
            ? <MobileBooksRoute />
            : <WithHeaderFooter element={<BooksListPage />} />
        }
        path="/books/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MobileBooksRoute />
            : <WithHeaderFooter element={<BooksListPage />} />
        }
        path="/books/author/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MobileBooksRoute />
            : <WithHeaderFooter element={<BooksListPage />} />
        }
        path="/books/language/:langid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MobileBooksRoute />
            : <WithHeaderFooter element={<BooksListPage />} />
        }
        path="/books/category/:catid"
      ></Route>

      {/* Redirects from old mobile-only routes to unified routes */}
      <Route element={<Navigate to="/books" replace />} path="/mbBooks" />
      <Route element={<Navigate to="/books" replace />} path="/readEBooks" />
      <Route element={<Navigate to="/books" replace />} path="/eBooks" />
      <Route element={<Navigate to="/videos" replace />} path="/video" />
      <Route
        element={<NavigatePreserveState to="/videos" />}
        path="/allVideos"
      />
      <Route element={<Navigate to="/magazines" replace />} path="/mobileMagazines" />
      <Route element={<Navigate to="/magazines/kalyan" replace />} path="/mobileKalyans" />
      <Route element={<Navigate to="/pravachans" replace />} path="/mobilePravachans" />

      {/* Mobile-only routes */}
      {isMobile && (
        <>
          <Route element={<MobileBooksFilterPage />} path="/filter" />
          <Route element={<MobileFavoriatePage />} path="/myFavourite" />
          <Route element={<MobileProfilePage />} path="/myProfile" />
          <Route element={<MobileDivineQuotes />} path="/divineQuotes" />
          <Route element={<DivineQuotesImageview />} path="/divineQuotesImg" />

          <Route element={<FilteredSubjectData />} path="/subjectData" />
          <Route element={<MobileRecentlyViewed />} path="/recentlyViewed" />
          <Route element={<ReadArticles />} path="/readArticles" />
          <Route element={<MobileLoginPage />} path="/loginPage" />

          <Route element={<MobileAudioListPage />} path="/mobileAudioListPage" />
          <Route element={<CategoriesFilter />} path="/categoriesFilter" />
          <Route element={<MbAudioPlayerPage />} path="/audioPlayerPage" />
          <Route element={<MbAudioCategory />} path="/mbAudioCategory" />
          <Route element={<Navigate to="/articles" replace />} path="/mobileArticles" />
          <Route element={<MobileTermsOfUse />} path="/terms-of-use" />
          <Route element={<MobilePrivacyPolicy />} path="/privacy-policy" />
          <Route element={<MobilePromotion />} path="/publicity" />
          <Route element={<MobileEnglishTemplate />} path="/pamphletEng" />
          <Route element={<MobileHindiTemplate />} path="/pamphletHindi" />
          <Route element={<RedirectLegendsIntroduction />} path="/legendsIntroduction" />
        </>
      )}

      {/* Book Detail */}
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<BookDetailPage />} />
        }
        path="/books/:bookid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<BookDetailPage />} />
        }
        path="/books/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<BookDetailPage />} />
        }
        path="/books/special/:bookid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<BookDetailPage />} />
        }
        path="/books/author/:authorId/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<BookDetailPage />} />
        }
        path="/books/language/:langId/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<BookDetailPage />} />
        }
        path="/books/category/:catId/:slug"
      ></Route>

      {/* epub */}
      <Route element={
        isMobile
          ? <MobileEpub />
          : <React.Suspense fallback={<div>Loading...</div>}>
              <EpubPage />
            </React.Suspense>
      } path="reader/:type/:id"></Route>

      {/* vivekvani listing — dedicated mobile list page */}
      <Route
        element={
          isMobile
            ? <MVivekvaniListPage />
            : <WithHeaderFooter element={<VivekvaniPage />} />
        }
        path="/magazines/vivekvani"
      ></Route>
      <Route
        element={
          isMobile
            ? <MVivekvaniListPage />
            : <WithHeaderFooter element={<VivekvaniPage />} />
        }
        path="/magazines/vivekvani/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MVivekvaniListPage />
            : <WithHeaderFooter element={<VivekvaniPage />} />
        }
        path="/magazines/vivekvani/author/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MVivekvaniListPage />
            : <WithHeaderFooter element={<VivekvaniPage />} />
        }
        path="/magazines/vivekvani/language/:langid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MVivekvaniListPage />
            : <WithHeaderFooter element={<VivekvaniPage />} />
        }
        path="/magazines/vivekvani/:cat/:langid"
      ></Route>

      {/* Vivek Vani Detail */}
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<VivekvaniDetailPage />} />
        }
        path="/magazines/vivekvani/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<VivekvaniDetailPage />} />
        }
        path="/magazines/vivekvani/special/:vaniid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<VivekvaniDetailPage />} />
        }
        path="/magazines/vivekvani/author/:id/:vaniid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<VivekvaniDetailPage />} />
        }
        path="/magazines/vivekvani/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<VivekvaniDetailPage />} />
        }
        path="/magazines/vivekvani/language/:langid"
      ></Route>

      {/* Kalyan */}
      <Route
        element={
          isMobile
            ? <MKalyanRoute />
            : <WithHeaderFooter element={<KalyansPage />} />
        }
        path="/magazines/kalyan"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalyanListPage />
            : <WithHeaderFooter element={<KalyansPage />} />
        }
        path="/magazines/kalyan/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalyanListPage />
            : <WithHeaderFooter element={<KalyansPage />} />
        }
        path="/magazines/kalyan/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalyanListPage />
            : <WithHeaderFooter element={<KalyansPage />} />
        }
        path="/magazines/kalyan/author/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalyanListPage />
            : <WithHeaderFooter element={<KalyansPage />} />
        }
        path="/magazines/kalyan/language/:langid"
      ></Route>

      {/* Kalyan Detail — mobile uses magazine home */}
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalyanDetailPage />} />
        }
        path="/magazines/kalyan/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalyanDetailPage />} />
        }
        path="/magazines/kalyan/special/:kalyanid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalyanDetailPage />} />
        }
        path="/magazines/kalyan/author/:id/:kalyanid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalyanDetailPage />} />
        }
        path="/magazines/kalyan/category/:catid/:kalyanid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalyanDetailPage />} />
        }
        path="/magazines/kalyan/language/:langid/:kalyanid"
      ></Route>

      {/* Kalyanakalpataru Page */}
      <Route
        element={
          isMobile
            ? <MKalpataruRoute />
            : <WithHeaderFooter element={<KalpataruPage />} />
        }
        path="/magazines/kalyanakalpataru"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalpataruListPage />
            : <WithHeaderFooter element={<KalpataruPage />} />
        }
        path="/magazines/kalyanakalpataru/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalpataruListPage />
            : <WithHeaderFooter element={<KalpataruPage />} />
        }
        path="/magazines/kalyanakalpataru/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalpataruListPage />
            : <WithHeaderFooter element={<KalpataruPage />} />
        }
        path="/magazines/kalyanakalpataru/author/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MKalpataruListPage />
            : <WithHeaderFooter element={<KalpataruPage />} />
        }
        path="/magazines/kalyanakalpataru/language/:langid"
      ></Route>

      {/* Kalpataru Detail — mobile uses magazine home */}
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalpataruDetailPage />} />
        }
        path="/magazines/kalyanakalpataru/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalpataruDetailPage />} />
        }
        path="/magazines/kalyanakalpataru/special/:kalyanid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalpataruDetailPage />} />
        }
        path="/magazines/kalyanakalpataru/author/:id/:kalyanid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalpataruDetailPage />} />
        }
        path="/magazines/kalyanakalpataru/category/:catid/:kalyanid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MMagazineHomePage />
            : <WithHeaderFooter element={<KalpataruDetailPage />} />
        }
        path="/magazines/kalyanakalpataru/language/:langid/:kalyanid"
      ></Route>

      {/* Geet Govind — mobile hub vs list via mGeetGovindRoute */}
      <Route
        element={
          isMobile
            ? <MGeetGovindRoute />
            : <WithHeaderFooter element={<GeetgovindPage />} />
        }
        path="/magazines/geetgovind"
      ></Route>
      <Route
        element={
          isMobile
            ? <MGeetGovindListPage />
            : <WithHeaderFooter element={<GeetgovindPage />} />
        }
        path="/magazines/geetgovind/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MGeetGovindListPage />
            : <WithHeaderFooter element={<GeetgovindPage />} />
        }
        path="/magazines/geetgovind/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MGeetGovindListPage />
            : <WithHeaderFooter element={<GeetgovindPage />} />
        }
        path="/magazines/geetgovind/author/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MGeetGovindListPage />
            : <WithHeaderFooter element={<GeetgovindPage />} />
        }
        path="/magazines/geetgovind/language/:langid"
      ></Route>

      {/* GeetGovindDetailPage */}
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<GeetGovindDetailPage />} />
        }
        path="/magazines/geetgovind/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<GeetGovindDetailPage />} />
        }
        path="/magazines/geetgovind/special/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<GeetGovindDetailPage />} />
        }
        path="/magazines/geetgovind/category/:catid/:id"
      ></Route>
      <Route
        element={
          isMobile
            ? <MBookDetailPage />
            : <WithHeaderFooter element={<GeetGovindDetailPage />} />
        }
        path="/magazines/geetgovind/language/:langid/:id"
      ></Route>

      {/* Unknown /magazines/* paths (e.g. /magazines/kalyandfdfd45454) go to magazines listing */}
      <Route element={<Navigate to="/magazines" replace />} path="/magazines/*" />

      {/* pravachan page */}
      <Route
        element={
          isMobile
            ? (() => {
                const MobilePravachanWrapper = () => {
                  const location = useLocation();
                  return (location.state as { type?: string })?.type === "pravachans"
                    ? <MobileAudioListPage />
                    : <MPravachanHubPage />;
                };
                return <MobilePravachanWrapper />;
              })()
            : <WithHeaderFooter element={<PravachansPage />} />
        }
        path="/pravachans"
      ></Route>
      <Route
        element={
          isMobile
            ? <MPravachanHubPage />
            : <WithHeaderFooter element={<PravachansPage />} />
        }
        path="/pravachans/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MPravachanHubPage />
            : <WithHeaderFooter element={<PravachansPage />} />
        }
        path="/pravachans/author/:id"
      ></Route>
      {/* Category/language list routes must sit before /pravachans/:slug and /pravachans/:cat/:id */}
      <Route
        element={
          isMobile
            ? <MPravachanHubPage />
            : <WithHeaderFooter element={<PravachansPage />} />
        }
        path="/pravachans/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MPravachanHubPage />
            : <WithHeaderFooter element={<PravachansPage />} />
        }
        path="/pravachans/language/:langid"
      ></Route>

      {/* pravachan detail page — REMOVE_NAVIGATION Phase 4 (desktop only)
          Mounts: /pravachans/:slug|/pravachans/:id → AudioPlayerPageNew
                  /pravachans/:cat/:id → AudioPlayerPage (legacy)
          Mobile MbAudioPlayerPage / mPravachanHubPage unchanged. */}
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <WithHeaderFooter element={<AudioPlayerPageNew />} />
      } path="/pravachans/:slug"></Route>
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <WithHeaderFooter element={<AudioPlayerPageNew />} />
      } path="/pravachans/:id"></Route>
      <Route element={<AudiosDetailPage />} path="/pravachans/:cat/:id"></Route>

      {/* audio page */}
      <Route
        element={
          isMobile
            ? (() => {
                const MobileAudioWrapper = () => {
                  const location = useLocation();
                  return (location.state as { categoryId?: unknown })?.categoryId !== undefined
                    ? <MobileAudioListPage />
                    : <MbAudioCategory />;
                };
                return <MobileAudioWrapper />;
              })()
            : <WithHeaderFooter element={<AudiosPage />} />
        }
        path="/audios"
      ></Route>
      <Route
        element={
          isMobile
            ? <MobileAudioListPage />
            : <WithHeaderFooter element={<AudiosPage />} />
        }
        path="/audios/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MobileAudioListPage />
            : <WithHeaderFooter element={<AudiosPage />} />
        }
        path="/audios/author/:id"
      ></Route>
      {/* Category/language list routes must sit before /audios/:slug and /audios/:cat/:id */}
      <Route
        element={
          isMobile
            ? <MobileAudioListPage />
            : <WithHeaderFooter element={<AudiosPage />} />
        }
        path="/audios/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MobileAudioListPage />
            : <WithHeaderFooter element={<AudiosPage />} />
        }
        path="/audios/language/:langid"
      ></Route>

      {/* audio player page */}
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <WithHeaderFooter element={<AudioPlayerPageNew />} />
      } path="/audios/:slug"></Route>
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <WithHeaderFooter element={<AudioPlayerPageNew />} />
      } path="/audios/:id"></Route>
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <AudiosDetailPage />
      } path="/audios/:cat/:id"></Route>
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <WithHeaderFooter element={<AudioPlayerPageNew />} />
      } path="/DailySatsang/:slug"></Route>
      <Route element={
        isMobile
          ? <MbAudioPlayerPage />
          : <WithHeaderFooter element={<AudioPlayerPageNew />} />
      } path="/DailySatsang/:id"></Route>
      <Route element={<AudiosDetailPage />} path="/DailySatsang/*"></Route>

      {/* daily satsang page */}
      <Route
        element={
          isMobile
            ? (() => {
                const MobileDailySatsangWrapper = () => {
                  const location = useLocation();
                  return (location.state as { type?: string })?.type === "DailySatsang"
                    ? <MobileAudioListPage />
                    : <DailySatsangPage />;
                };
                return <MobileDailySatsangWrapper />;
              })()
            : <WithHeaderFooter element={<DailySatsangPage />} />
        }
        path="/DailySatsang"
      ></Route>

      {/* video player page */}
      <Route
        element={
          isMobile
            ? (() => {
                const MobileVideoWrapper = () => {
                  const location = useLocation();
                  const state = location.state as {
                    id?: unknown;
                    categoryId?: unknown;
                    authorId?: unknown;
                    videoId?: unknown;
                  } | null;
                  const hasFilter =
                    state?.id !== undefined ||
                    state?.categoryId !== undefined ||
                    state?.authorId !== undefined ||
                    state?.videoId !== undefined;
                  return hasFilter ? <MbAllVideos /> : <MbVideoParentCategory />;
                };
                return <MobileVideoWrapper />;
              })()
            : <WithHeaderFooter element={<VideoListPage />} />
        }
        path="/videos/*"
      ></Route>
      <Route
        element={<WithHeaderFooter element={<VideoDetailsPage />} />}
        path="/videos/:slug"
      ></Route>
      {isMobile && (
        <>
          <Route
            element={<MbAllVideos />}
            path="/videos/author/:id"
          ></Route>
          <Route
            element={<MbAllVideos />}
            path="/videos/category/:id"
          ></Route>
          <Route
            element={<MbAllVideos />}
            path="/videos/video/:id"
          ></Route>
        </>
      )}

      {/* article page — mobile list uses mArticlesPage */}
      <Route
        element={
          isMobile
            ? <MArticlesPage />
            : <WithHeaderFooter element={<ArticlesPage />} />
        }
        path="/articles"
      ></Route>
      <Route
        element={
          isMobile
            ? <MArticlesPage />
            : <WithHeaderFooter element={<ArticlesPage />} />
        }
        path="/articles/special"
      ></Route>
      <Route
        element={
          isMobile
            ? <MArticlesPage />
            : <WithHeaderFooter element={<ArticlesPage />} />
        }
        path="/articles/author/:id"
      ></Route>
      {/* Category/language list routes must sit before /articles/:slug */}
      <Route
        element={
          isMobile
            ? <MArticlesPage />
            : <WithHeaderFooter element={<ArticlesPage />} />
        }
        path="/articles/category/:catid"
      ></Route>
      <Route
        element={
          isMobile
            ? <MArticlesPage />
            : <WithHeaderFooter element={<ArticlesPage />} />
        }
        path="/articles/language/:langid"
      ></Route>

      {/* article detail page — canonical /articles/:slug; nested paths redirect */}
      <Route
        element={<RedirectToCanonicalArticle slugParam="slug" />}
        path="/articles/category/:catId/:slug"
      ></Route>
      <Route
        element={<RedirectToCanonicalArticle slugParam="slug" />}
        path="/articles/language/:langId/:slug"
      ></Route>
      <Route
        element={<RedirectToCanonicalArticle slugParam="articleid" />}
        path="/articles/author/:id/:articleid"
      ></Route>
      <Route
        element={
          isMobile
            ? <ReadArticles />
            : <WithHeaderFooter element={<ArticlesDetailPage />} />
        }
        path="/articles/:slug"
      ></Route>
      <Route
        element={
          isMobile
            ? <ReadArticles />
            : <WithHeaderFooter element={<ArticlesDetailPage />} />
        }
        path="/articles/special/:id"
      ></Route>

      {/* Divine Quotes Page */}
      <Route
        element={
          isMobile
            ? <MobileDivineQuotes />
            : <WithHeaderFooter element={<DivineQuotes />} />
        }
        path="/divinequotes"
      ></Route>
      <Route
        element={
          isMobile
            ? <MDivineQuotes />
            : <WithHeaderFooter element={<QuoteDetailsPage />} />
        }
        path="/divinequotes/:id"
      ></Route>

      {/* Messages / Amrit Vachan board — BannerFor 10 / 11 */}
      <Route
        element={<WithHeaderFooter element={<MessagesPage />} />}
        path="/messages"
      ></Route>
      <Route
        element={<WithHeaderFooter element={<MessageDetailPage />} />}
        path="/messages/:slug"
      ></Route>

      {/* Privacy Policy */}
      <Route
        element={
          isMobile
            ? <MobilePrivacyPolicy />
            : <WithHeaderFooter element={<PrivacyPage />} />
        }
        path="/privacypolicy"
      ></Route>

      {/* Terms of Use */}
      <Route
        element={
          isMobile
            ? <MobileTermsOfUse />
            : <WithHeaderFooter element={<TearmsOfUsePage />} />
        }
        path="/termsofuse"
      ></Route>

      {/* FAQ — desktop only */}
      <Route
        element={<WithHeaderFooter element={<FaqPage />} />}
        path="/faq"
      ></Route>

      {/* Promotion — desktop only */}
      <Route
        element={<WithHeaderFooter element={<PromotionPage />} />}
        path="/promotion"
      ></Route>

      {/* Author profile — mobile: mAuthorDetailsPage, desktop: AuthorsPage */}
      <Route
        element={
          isMobile
            ? <MAuthorDetailsPage />
            : <WithHeaderFooter element={<AuthorsDataPage />} />
        }
        path="/author/:id"
      ></Route>

      {/* Search — desktop only */}
      <Route
        element={<WithHeaderFooter element={<SearchDataPage />} />}
        path="/searchdata/:searchCategory/:search"
      ></Route>
      <Route
        element={<WithHeaderFooter element={<SearchDataPage />} />}
        path="/searchdata/:searchCategory/"
      ></Route>
      <Route
        element={<WithHeaderFooter element={<SearchDataPage />} />}
        path="/searchdata/:searchCategory/*"
      ></Route>

      {/* Profile */}
      <Route
        element={
          isMobile
            ? <MobileProfilePage />
            : <WithHeaderFooter element={<Profile />} />
        }
        path="/profile/"
      ></Route>

      {/* Login */}
      <Route
        element={
          isMobile
            ? <MobileLoginPage />
            : <NewLoginPage />
        }
        path="/Login/*"
      ></Route>

      {/* Profile Favourites */}
      <Route
        element={
          isMobile
            ? <MobileFavoriatePage />
            : <WithHeaderFooter element={<ProfileFav />} />
        }
        path="/profile/My-Favourites"
      ></Route>

      {/* Profile Feedback — desktop only */}
      <Route
        element={<WithHeaderFooter element={<ProfileContact />} />}
        path="/profile/feedback"
      ></Route>

      {/* Pdf Viewer — desktop only */}
      <Route element={<PDFViewer />} path="/pdf-viewer"></Route>

      {/* fallback: redirect unknown URLs back to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};
export default AppRoutes;
